import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { PERSON_CALLS } from '../../../shared/people/person-calls';

/**
 * im-call-presence-dot — a colleague's live presence on their avatar (beads indoor_mapping_poc-0sm8.4, ticket 1.18).
 *
 * Fed by the `presenceChanged` subscription through PERSON_CALLS: in a call, in the office, at home or elsewhere,
 * out. Drawn at the bottom-right of its parent (give the parent `position: relative`), with the words for it beside
 * the dot for assistive technology, so the state never rests on colour alone. Nothing at all when calling is off
 * (`flow.calls`) or nothing is known.
 *
 *   <span class="avatar">…<im-call-presence-dot [personId]="person.id" /></span>
 */
@Component({
  selector: 'im-call-presence-dot',
  standalone: true,
  imports: [TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
  template: `
    @if (kind(); as k) {
      <span class="dot" aria-hidden="true" data-testid="emp.calls.presence.dot" [attr.data-kind]="k" [attr.data-person]="personId()"></span>
      <span class="sr-only">{{ 'flow_call.presence.' + k | transloco }}</span>
    }
  `,
  styles: `
    :host { position: absolute; right: -1px; bottom: -1px; display: block; line-height: 0; pointer-events: none; }
    .dot {
      display: block;
      width: var(--call-presence-dot, 10px);
      aspect-ratio: 1;
      box-sizing: border-box;
      border-radius: 50%;
      border: 2px solid var(--call-presence-ring, var(--surface-raised, var(--card-bg-solid)));
      background: var(--text-tertiary);
    }
    .dot[data-kind='office'] { background: var(--success-strong, var(--avail-available)); }
    .dot[data-kind='home'], .dot[data-kind='elsewhere'] { background: var(--text-secondary); }
    .dot[data-kind='out'] { background: var(--call-presence-ring, var(--surface-raised, var(--card-bg-solid))); box-shadow: inset 0 0 0 1.5px var(--text-tertiary); }
    .dot[data-kind='call'] { background: var(--avail-booked, var(--danger)); }
  `,
})
export class CallPresenceDotComponent {
  private readonly calls = inject(PERSON_CALLS);

  readonly personId = input.required<string>();

  protected readonly kind = computed(() => this.calls.presence(this.personId())?.kind ?? null);

  constructor() {
    // Ask for their presence while the dot is on screen; the release runs when the id changes or the dot goes.
    effect((onCleanup) => {
      const id = this.personId();
      if (!this.calls.enabled() || !id) return;
      const release = this.calls.watch(id);
      onCleanup(release);
    });
  }
}
